import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { useClientLoginMutation } from "../../../store/service/clientBetService";
import PreLoginLobby from "../../common/preLoginLobby/PreLoginLobby";

const ClientLogin = () => {
  const nav = useNavigate();
  const [login, { isLoading }] = useClientLoginMutation();
  const [showLogin, setShowLogin] = useState(false);
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [authTip, setAuthTip] = useState("");

  const clearAuthTip = () => {
    if (authTip) setAuthTip("");
    if (fieldErrors.auth) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.auth;
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    if (!userId.trim()) next.userId = "Enter your User ID";
    if (!password.trim()) next.password = "Enter your password";
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setAuthTip("");
    if (!validate()) return;

    try {
      const res = await login({
        userId: userId.trim(),
        password: password.trim(),
      }).unwrap();

      if (res?.token) {
        localStorage.setItem("client-token", res.token);
        localStorage.setItem("client-userId", res.userId);
        localStorage.setItem("client-username", res.username);
        nav("/play");
      }
    } catch (err) {
      const msg = err?.data?.message || "Invalid User ID or password";
      setAuthTip(msg);
      setFieldErrors({ userId: true, password: true, auth: true });
    }
  };

  return (
    <PreLoginLobby
      loginOpen={showLogin}
      onOpenLogin={() => setShowLogin(true)}
      onCloseLogin={() => setShowLogin(false)}
      loginModal={
        <form className="w777-login-panel" onSubmit={onSubmit} noValidate>
          <button type="button" className="w777-close-login" onClick={() => setShowLogin(false)}>
            ×
          </button>
          <h2 className="w777-login-title">Login</h2>

          {authTip ? (
            <div className="w777-error-tip" role="alert">
              {authTip}
            </div>
          ) : null}

          <div className="w777-field">
            <label className="w777-label" htmlFor="client-user-id">
              <span className="w777-req">*</span> User ID
            </label>
            <input
              id="client-user-id"
              className={`w777-input ${fieldErrors.userId && typeof fieldErrors.userId === "string" ? "is-error" : ""} ${fieldErrors.auth ? "is-error" : ""}`}
              type="text"
              placeholder="Your User ID"
              autoComplete="username"
              value={userId}
              onChange={(ev) => {
                setUserId(ev.target.value);
                clearAuthTip();
                if (fieldErrors.userId) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.userId;
                    return next;
                  });
                }
              }}
            />
            {typeof fieldErrors.userId === "string" ? (
              <p className="w777-field-error">{fieldErrors.userId}</p>
            ) : null}
          </div>

          <div className="w777-field">
            <label className="w777-label" htmlFor="client-password">
              <span className="w777-req">*</span> Password
            </label>
            <div className="w777-input-wrap">
              <input
                id="client-password"
                className={`w777-input ${fieldErrors.password && typeof fieldErrors.password === "string" ? "is-error" : ""} ${fieldErrors.auth ? "is-error" : ""}`}
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                autoComplete="current-password"
                value={password}
                onChange={(ev) => {
                  setPassword(ev.target.value);
                  clearAuthTip();
                  if (fieldErrors.password) {
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.password;
                      return next;
                    });
                  }
                }}
              />
              <button
                type="button"
                className="w777-eye-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}>
                {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              </button>
            </div>
            {typeof fieldErrors.password === "string" ? (
              <p className="w777-field-error">{fieldErrors.password}</p>
            ) : null}
          </div>

          <button type="submit" className="w777-submit" disabled={isLoading}>
            {isLoading ? <Spin size="small" /> : "Login"}
          </button>

          <p className="w777-login-hint">Use the User ID and password shared by your agent.</p>
        </form>
      }
    />
  );
};

export default ClientLogin;
